import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMinus, FiPlus, FiX, FiShoppingBag, FiArrowRight } from 'react-icons/fi';
import { getCart, updateCartItemQuantity, removeFromCart } from '../lib/cart';
import { proceedToCheckout } from '../lib/checkout';
import toast from 'react-hot-toast';
import { useCurrency } from '../context/CurrencyContext';

export default function Cart() {
  const { formatPrice, currency, exchangeRate } = useCurrency();
  const [cart, setCart] = useState({ items: [], total: 0, count: 0 });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const updateCart = () => {
      setCart(getCart());
    };
    updateCart();
    window.addEventListener('cartUpdated', updateCart);
    return () => {
      window.removeEventListener('cartUpdated', updateCart);
    };
  }, []);

  const updateQuantity = async (productId, newQuantity, variationId = null) => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      const updatedCart = updateCartItemQuantity(productId, newQuantity, variationId);
      setCart(updatedCart);
      window.dispatchEvent(new Event('cartUpdated'));
      toast.success('Cart updated');
    } catch (error) {
      toast.error('Failed to update cart');
    } finally {
      setIsUpdating(false);
    }
  };

  const removeItem = async (productId, variationId = null) => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      const updatedCart = removeFromCart(productId, variationId);
      setCart(updatedCart);
      window.dispatchEvent(new Event('cartUpdated'));
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    } finally {
      setIsUpdating(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <FiShoppingBag size={64} className="text-gray-600 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-white mb-4">Your cart is empty</h1>
            <p className="text-gray-400 text-lg mb-8">
              Add some beautiful dresses or jewelry to get started
            </p>
            <Link
              to="/collections"
              className="inline-flex items-center px-8 py-4 bg-pink-500 text-black font-semibold rounded-lg hover:bg-pink-600 transition-colors"
            >
              Start Shopping
              <FiArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Shopping Cart</h1>
          <p className="text-gray-400">
            {cart.count} {cart.count === 1 ? 'item' : 'items'} in your cart
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-800">
              <div className="space-y-6">
                {cart.items.map((item) => (
                  <div
                    key={`${item.id}-${item.variation_id || 'default'}`}
                    className="flex space-x-4 p-4 bg-gray-800 rounded-xl"
                  >
                    <div className="flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        width={120}
                        height={120}
                        className="w-30 h-30 object-cover rounded-lg"
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-white mb-2">{item.name}</h3>

                      {/* Variation details */}
                      {item.variation_id && item.attributes && item.attributes.length > 0 && (
                        <div className="mb-2">
                          {item.attributes.map((attr, index) => (
                            <span key={index} className="text-sm text-gray-400">
                              {attr.name}: {attr.option}
                              {index < item.attributes.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {/* Quantity Controls */}
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1, item.variation_id)}
                              disabled={isUpdating || item.quantity <= 1}
                              className="p-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FiMinus size={16} />
                            </button>
                            <span className="text-white font-semibold px-3 py-1 bg-gray-700 rounded-lg min-w-[3rem] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1, item.variation_id)}
                              disabled={isUpdating}
                              className="p-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FiPlus size={16} />
                            </button>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-lg font-bold text-pink-500">
                            {formatPrice(item.price * item.quantity)}
                          </div>
                          {item.quantity > 1 && (
                            <div className="text-sm text-gray-400">
                              {formatPrice(item.price)} each
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removeItem(item.id, item.variation_id)}
                      disabled={isUpdating}
                      className="flex-shrink-0 p-2 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                      title="Remove item"
                    >
                      <FiX size={20} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-800 sticky top-8">
              <h2 className="text-2xl font-bold text-white mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-300">
                  <span>Subtotal ({cart.count} items)</span>
                  <span>{formatPrice(cart.total)}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Shipping</span>
                  <span className="text-green-400">Free</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Tax (8%)</span>
                  <span>{formatPrice(cart.total * 0.08)}</span>
                </div>
                <div className="border-t border-gray-700 pt-4">
                  <div className="flex justify-between text-2xl font-bold text-white">
                    <span>Total</span>
                    <span>{formatPrice(cart.total + cart.total * 0.08)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => proceedToCheckout(currency, exchangeRate)}
                  className="w-full inline-flex items-center justify-center px-6 py-4 bg-pink-500 text-black font-semibold rounded-lg hover:bg-pink-600 transition-colors"
                >
                  Proceed to Checkout
                  <FiArrowRight className="ml-2" size={20} />
                </button>

                <Link
                  to="/collections"
                  className="w-full inline-flex items-center justify-center px-6 py-4 text-white font-semibold rounded-lg border border-gray-600 hover:border-pink-500 transition-colors"
                >
                  Continue Shopping
                </Link>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-700">
                <div className="text-center text-sm text-gray-400">
                  <p className="mb-2">Secure checkout with SSL encryption</p>
                  <p>Free shipping on all orders</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
